import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import GetAppIcon from "@material-ui/icons/GetApp";
import CloseIcon from "@material-ui/icons/Close";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import CircularProgress from "@material-ui/core/CircularProgress";
import Tooltip from "@material-ui/core/Tooltip";
import A_TextField from "../../../Atoms/Inputs/TextFields/A_TextField";
import A_IconButton from "../../../Atoms/Inputs/Buttons/A_IconButton";
import M_FirmPicker from "../../../Molecules/Common/ProfessionalContacts/M_FirmPicker";
import { canAddQuote, quotesForType, MAX_QUOTES_PER_TYPE, QUOTE_DOCUMENT_ALLOWED_EXTENSIONS, isAllowedQuoteDocumentExtension, isQuoteDocumentTooLarge, QUOTE_DOCUMENT_MAX_SIZE_BYTES } from "../../../../../domain/professionalContacts/professionalContacts.rules";
import { QUOTE_TYPES, QUOTE_TYPE_TO_LOAN_ROLE } from "../../../../../domain/professionalContacts/professionalContacts.types";
import { uploadQuoteDocument, downloadQuoteDocument } from "../../../../../Utils/apis/professionalContactsApi";
import useStyles from "./ProfessionalDetails.styles";

const ACCEPT_ATTR = QUOTE_DOCUMENT_ALLOWED_EXTENSIONS.join(",");
const MAX_SIZE_MB = Math.round(QUOTE_DOCUMENT_MAX_SIZE_BYTES / (1024 * 1024));

/** Third-party quotes editor (§4b, DF177): up to MAX_QUOTES_PER_TYPE per type, firm picker + £ value.
 * Each quote row also carries an optional supporting document (upload/download/replace) filed
 * under Loan Documents' fixed "Quotes" category — see
 * docs/planning/professional-contacts-quote-documents/00-overview.md for the full contract. */
const QuotesEditor = ({ loanId, quotes, onChange }) => {
  const classes = useStyles();
  const oidcUser = useSelector((state) => state.oidc && state.oidc.user);
  const fileInputRefs = useRef({});
  // Keyed by quote index — per-row upload state so one row's failure/spinner never blocks another.
  const [documentState, setDocumentState] = useState({});

  const addQuote = (type) => {
    const role = QUOTE_TYPE_TO_LOAN_ROLE[type];
    if (!canAddQuote(quotes, role)) return;
    onChange([...quotes, { role, firmId: null, firmName: "", value: "", currency: "GBP", document: null }]);
  };

  const removeQuote = (targetIndex) => onChange(quotes.filter((_, index) => index !== targetIndex));

  const updateQuote = (targetIndex, patch) =>
    onChange(quotes.map((quote, index) => (index === targetIndex ? { ...quote, ...patch } : quote)));

  const setRowState = (index, patch) =>
    setDocumentState((prev) => ({ ...prev, [index]: { ...prev[index], ...patch } }));

  const triggerFilePicker = (index) => {
    const input = fileInputRefs.current[index];
    if (input) input.click();
  };

  const handleFileSelected = async (index, event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = ""; // allow re-selecting the same file name after a remove/replace
    if (!file) return;

    if (!isAllowedQuoteDocumentExtension(file.name)) {
      setRowState(index, { error: `Unsupported file type. Allowed: ${QUOTE_DOCUMENT_ALLOWED_EXTENSIONS.join(", ")}` });
      return;
    }
    if (isQuoteDocumentTooLarge(file)) {
      setRowState(index, { error: `File is too large. Maximum size is ${MAX_SIZE_MB} MB.` });
      return;
    }

    setRowState(index, { error: null, uploading: true });
    try {
      const document = await uploadQuoteDocument(loanId, file, oidcUser);
      updateQuote(index, { document });
      setRowState(index, { uploading: false, error: null });
    } catch (err) {
      setRowState(index, { uploading: false, error: "Upload failed. Please try again." });
    }
  };

  const handleDownload = (quote, index) => {
    if (!quote.document) return;
    setRowState(index, { downloading: true, error: null });
    downloadQuoteDocument(quote.document, oidcUser)
      .catch(() => setRowState(index, { error: "Download failed. Please try again." }))
      .finally(() => setRowState(index, { downloading: false }));
  };

  const handleRemoveDocument = (index) => {
    // Clears the reference on the quote only — matches the existing app's own upload/detach
    // behaviour elsewhere; the underlying blob isn't deleted from here (see 00-overview.md §BE).
    updateQuote(index, { document: null });
    setRowState(index, { error: null });
  };

  return (
    <div className={classes.grid3}>
      {QUOTE_TYPES.map((type) => {
        const role = QUOTE_TYPE_TO_LOAN_ROLE[type];
        const rows = quotesForType(quotes, role).map((quote) => ({ quote, index: quotes.indexOf(quote) }));
        const canAdd = canAddQuote(quotes, role);
        return (
          <div key={type}>
            <div className={classes.subtle} style={{ marginBottom: 5 }}>
              {type} <span>({rows.length}/{MAX_QUOTES_PER_TYPE})</span>
            </div>
            {rows.length === 0 && <div className={classes.subtle}>No quotes yet.</div>}
            {rows.map(({ quote, index }) => {
              const rowState = documentState[index] || {};
              return (
                <React.Fragment key={index}>
                  <div className={classes.quoteRow}>
                    <div style={{ flex: 1 }}>
                      <M_FirmPicker
                        id={`quote-firm-${type}-${index}`}
                        label="Firm"
                        loanId={loanId}
                        role={role}
                        value={quote.firmId}
                        onChange={(firm) => updateQuote(index, { firmId: firm?.id || null, firmName: firm?.name || "" })}
                      />
                    </div>
                    <A_TextField
                      id={`quote-value-${type}-${index}`}
                      label="£ value"
                      type="number"
                      defaultValue={quote.value}
                      onChange={(e) => updateQuote(index, { value: e.target.value })}
                      size="small"
                      variant="outlined"
                      style={{ width: 130 }}
                    />
                    <A_IconButton size="small" icon={<DeleteIcon fontSize="small" />} ariaLabel="Remove quote" onClick={() => removeQuote(index)} />
                  </div>

                  <div className={classes.quoteDocumentRow}>
                    <input
                      ref={(el) => (fileInputRefs.current[index] = el)}
                      type="file"
                      accept={ACCEPT_ATTR}
                      style={{ display: "none" }}
                      onChange={(e) => handleFileSelected(index, e)}
                    />
                    {rowState.uploading ? (
                      <span className={classes.subtle} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                        <CircularProgress size={12} /> Uploading…
                      </span>
                    ) : quote.document ? (
                      <>
                        <Tooltip title={quote.document.fileName}>
                          <span className={classes.quoteDocumentChip}>
                            <InsertDriveFileIcon fontSize="inherit" />
                            {quote.document.fileName}
                          </span>
                        </Tooltip>
                        <Tooltip title="Download supporting document">
                          <span>
                            <A_IconButton
                              size="small"
                              icon={rowState.downloading ? <CircularProgress size={14} /> : <GetAppIcon fontSize="small" />}
                              ariaLabel={`Download supporting document for ${type} quote ${index + 1}`}
                              disabled={rowState.downloading}
                              onClick={() => handleDownload(quote, index)}
                            />
                          </span>
                        </Tooltip>
                        <Tooltip title="Remove document">
                          <span>
                            <A_IconButton
                              size="small"
                              icon={<CloseIcon fontSize="small" />}
                              ariaLabel={`Remove supporting document for ${type} quote ${index + 1}`}
                              onClick={() => handleRemoveDocument(index)}
                            />
                          </span>
                        </Tooltip>
                      </>
                    ) : (
                      <Tooltip title={`Attach a supporting document (${QUOTE_DOCUMENT_ALLOWED_EXTENSIONS.join(", ")})`}>
                        <span
                          className={classes.linkAction}
                          role="button"
                          tabIndex={0}
                          onClick={() => triggerFilePicker(index)}
                          onKeyPress={(e) => e.key === "Enter" && triggerFilePicker(index)}
                          aria-label={`Attach supporting document for ${type} quote ${index + 1}`}
                        >
                          <AttachFileIcon fontSize="inherit" /> Attach document
                        </span>
                      </Tooltip>
                    )}
                  </div>
                  {rowState.error && <div className={classes.quoteDocumentError}>{rowState.error}</div>}
                </React.Fragment>
              );
            })}
            {canAdd ? (
              <span className={classes.linkAction} onClick={() => addQuote(type)}>
                <AddIcon fontSize="inherit" /> Add quote
              </span>
            ) : (
              <span className={classes.subtle}>Maximum of {MAX_QUOTES_PER_TYPE} reached</span>
            )}
          </div>
        );
      })}
    </div>
  );
};

QuotesEditor.propTypes = {
  loanId: PropTypes.string.isRequired,
  quotes: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default QuotesEditor;
